/**
 * "Connection lost" state for a hub tab.
 *
 * When a session dies (token expired / hub unreachable) the tab used to close
 * itself and drop the user into the launcher. Instead the tab stays put and
 * shows an overlay offering Reconnect / Close — this store holds that state and
 * knows how to bring the same target back up.
 *
 * What "bring it back up" has to get right is **which hub, named by which id**:
 * this app holds two id spaces for one hub (`config/runtime.ts` →
 * `lostSessionHubIds`), and a reconnect that names the hub with an id no lookup
 * knows is not a reconnect — the ladder refuses without dialling anything, and
 * the tab was live a second earlier, so the account's way into it does exist.
 *
 * The route is not chosen here either: a local tab goes through
 * `connectResolvingRoute` (the account's own token at the hub's LAN address, the
 * tunnel only as its fallback — plan `LOCAL-FIRST-CONNECT.md` §2/D2 step 3),
 * which is what makes Reconnect work when the tab came up on the LAN and the LAN
 * is what went away.
 */
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useAuthStore } from './auth';
import { useSystemStore } from './system';
import { useWebRTCStore } from './webrtc';
import {
  getActiveHubEndpoint,
  getActiveHubOwnId,
  lostSessionHubIds,
  readTabActiveHub,
} from '../config/runtime';
import {
  connectCloudSilently,
  connectResolvingRoute,
  getHubResumePath,
  resolveLocalResumePath,
  storedLocalResumePath,
} from '../services/hubConnect';
import { formatHubOpenAddress, type HubOpenFailure } from '../utils/hubOpenFallback';

export type ConnectionLostReason = 'session' | 'offline';

interface LostTarget {
  mode: 'local' | 'cloud';
  ip?: string;
  port?: number;
  secure?: boolean;
  /** The id the reconnect names the hub by — the account's own row id when known. */
  hubId?: string;
  /** The other spelling (the hub's own id), which the claim's account lookup also accepts. */
  accountHubId?: string;
  name?: string;
}

export const useConnectionLostStore = defineStore('connectionLost', () => {
  const lost = ref(false);
  const reason = ref<ConnectionLostReason>('session');
  const hubName = ref<string | null>(null);
  /** The address the reconnect will dial (`host:port`), for the refusal's sentence. */
  const address = ref<string | null>(null);
  const reconnecting = ref(false);
  /** Why the reconnect did not open the hub, when its route refused (`hub.open.failure.*`). */
  const failure = ref<HubOpenFailure | null>(null);
  /** The hub's own refusal as an i18n key (`auth.claim.*`), when the hub gave one. */
  const messageKey = ref<string | null>(null);
  /** The last resort: a failure with no sentence of its own in that vocabulary. */
  const error = ref<string | null>(null);

  let target: LostTarget | null = null;

  /**
   * Where the tab was connected when the session dropped.
   *
   * The window's own record is read first (`readTabActiveHub` — the per-tab
   * marker): it carries the address, the name the connect was given and the id,
   * where the shared `localStorage` keys hold whichever tab wrote last.
   *
   * Both id spellings come out of here (`lostSessionHubIds`). This used to carry
   * none of them — the id was read from `otto_active_hub_id`, a key nothing in
   * this app writes — so the claim ladder answered `no-account-identity` without
   * dialling the hub, that refusal does not fall back to the tunnel, and the
   * overlay answered "No credential for this hub — join or adopt it" for a hub
   * the account owned and that was answering.
   *
   * `otto_active_hub_key` is the LAST resort for the id, and only for a window
   * that has no hub of its own to name: it is shared (`localStorage`), so in a
   * second tab it holds whatever another tab connected to last. A window whose own
   * marker names its hub must not answer a reconnect with another hub's id —
   * measured: the Kolomban tab's reconnect opened the Dev hub over the tunnel,
   * because the marker had no id and the shared key held `hub:517e7adb…`
   * (card `t_f000e669`).
   */
  function captureTarget(): LostTarget | null {
    const webrtcStore = useWebRTCStore();
    const tab = readTabActiveHub();
    // The marker's name, not the store's: a window can be on a hub whose name the
    // system store has not loaded.
    const name = tab?.name || useSystemStore().name || undefined;

    if (webrtcStore.connected && webrtcStore.hubId) {
      return { mode: 'cloud', hubId: webrtcStore.hubId, name };
    }

    let ip: string | null = null;
    let port = 0;
    let secure = false;
    // Per-tab: reconnect to the hub THIS window was on, not whichever hub the
    // shared storage was last written by another tab.
    const endpoint = tab ?? getActiveHubEndpoint();
    if (endpoint) {
      ip = endpoint.ip;
      port = endpoint.port;
      secure = endpoint.secure;
    }
    if (!ip || !port) return null;

    const ids = lostSessionHubIds({
      tabHubId: tab?.hubId,
      // The window named its own hub: the shared key is another tab's write, so it
      // is not asked at all. It is still the fallback for a window that has no
      // marker of its own (a legacy window, or one that never connected).
      ownHubId: tab?.hubId ? null : getActiveHubOwnId(),
    });
    return { mode: 'local', ip, port, secure, hubId: ids.hubId, accountHubId: ids.accountHubId, name };
  }

  function markLost(nextReason: ConnectionLostReason = 'session'): void {
    if (lost.value) return;
    target = captureTarget();
    hubName.value = target?.name ?? null;
    address.value = target?.ip && target?.port ? formatHubOpenAddress({ ip: target.ip, port: target.port }) : null;
    reason.value = nextReason;
    failure.value = null;
    messageKey.value = null;
    error.value = null;
    lost.value = true;
  }

  function clear(): void {
    lost.value = false;
    error.value = null;
    failure.value = null;
    messageKey.value = null;
    reconnecting.value = false;
  }

  /** Re-establish the session that dropped, then resume where the tab was. */
  async function reconnect(): Promise<boolean> {
    if (reconnecting.value) return false;
    const authStore = useAuthStore();
    reconnecting.value = true;
    error.value = null;
    failure.value = null;
    messageKey.value = null;

    try {
      const resolved = target ?? captureTarget();
      if (!resolved) {
        error.value = 'No server to reconnect to';
        return false;
      }

      // Where the tab was, read BEFORE the dead session is dropped: `logout()`
      // clears this hub's stored position, and Reconnect promises the tab keeps
      // its place.
      const resumePath = resolved.mode === 'local' ? storedLocalResumePath() : null;

      // A stale token is useless — a local session drops it so the account's claim
      // is spent again. The cloud session (what a claim is spent WITH) is not
      // touched by `logout()`.
      if (resolved.mode === 'local') authStore.logout();

      const result =
        resolved.mode === 'cloud'
          ? await connectCloudSilently({ mode: 'cloud', hubId: resolved.hubId, name: resolved.name })
          : await connectResolvingRoute({
              mode: 'local',
              ip: resolved.ip,
              port: resolved.port,
              secure: resolved.secure,
              hubId: resolved.hubId,
              accountHubId: resolved.accountHubId,
              name: resolved.name,
            });

      if (!result.ok) {
        // The refusal is kept as a refusal, and the overlay paints its sentence
        // (`hub.open.failure.*`): `result.error` is the wire's own words about an
        // internal state ("No credential for this hub — join or adopt it"), which
        // is not something to say to the person at the screen.
        //
        // The cloud's own name wins over the LAN route's when it refused by name
        // (`cloudRefusal` — `remote_access_not_licensed`): it is the refusal the
        // reconnect ended on, and the reason no route is left.
        failure.value = result.cloudRefusal ?? result.localRefusal ?? null;
        messageKey.value = result.messageKey ?? null;
        error.value = result.error || 'Reconnect failed';
        return false;
      }

      const path = resumePath
        ? `/${resumePath.replace(/^\/+/, '')}`
        : resolved.mode === 'cloud' && resolved.hubId
          ? getHubResumePath(resolved.hubId)
          : await resolveLocalResumePath();
      clear();
      const router = (await import('../router')).default;
      await router.replace(path);
      return true;
    } catch (__caught: unknown) {
      const e = __caught as Error;
      error.value = e?.message || 'Reconnect failed';
      return false;
    } finally {
      reconnecting.value = false;
    }
  }

  return {
    lost,
    reason,
    hubName,
    address,
    reconnecting,
    failure,
    messageKey,
    error,
    markLost,
    clear,
    reconnect,
  };
});
